import { useState, useEffect } from 'react';
import { Subscription, SubscriptionTier, UsageTracking } from '../types/user';
import { users } from '../services/supabase';
import { useAuth } from './useAuth';

interface SubscriptionState {
  subscription: Subscription | null;
  usage: Record<string, UsageTracking>;
  loading: boolean;
  error: string | null;
}

export const useSubscription = () => {
  const { user } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscription: null,
    usage: {},
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!user) {
      setState({
        subscription: null,
        usage: {},
        loading: false,
        error: null,
      });
      return;
    }

    const fetchSubscription = async () => {
      try {
        const { data: subscription, error: subError } = await users.getUserSubscription(user.id);
        if (subError && subError.code !== 'PGRST116') { // Not found error
          throw subError;
        }

        // Fetch current usage
        const today = new Date().toISOString().split('T')[0];
        const { data: lgaUsage } = await users.getUsageTracking(user.id, 'lga_report', today);
        const { data: apiUsage } = await users.getUsageTracking(user.id, 'api_call', today);

        const usage: Record<string, UsageTracking> = {};
        if (lgaUsage) usage['lga_report'] = lgaUsage;
        if (apiUsage) usage['api_call'] = apiUsage;

        setState({
          subscription: subscription || null,
          usage,
          loading: false,
          error: null,
        });
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Failed to fetch subscription',
          loading: false,
        }));
      }
    };

    fetchSubscription();
  }, [user]);

  const canAccessResource = (resourceType: 'lga_report' | 'api_call'): boolean => {
    if (!state.subscription) return false;

    const limits = state.subscription;
    const currentUsage = state.usage[resourceType]?.count || 0;

    switch (resourceType) {
      case 'lga_report':
        return limits.monthly_lga_limit === -1 || currentUsage < limits.monthly_lga_limit;
      case 'api_call':
        return limits.api_rate_limit === -1 || currentUsage < limits.api_rate_limit;
      default:
        return false;
    }
  };

  const getRemainingUsage = (resourceType: 'lga_report' | 'api_call'): number | 'unlimited' => {
    if (!state.subscription) return 0;

    const limits = state.subscription;
    const currentUsage = state.usage[resourceType]?.count || 0;

    switch (resourceType) {
      case 'lga_report':
        if (limits.monthly_lga_limit === -1) return 'unlimited';
        return Math.max(0, limits.monthly_lga_limit - currentUsage);
      case 'api_call':
        if (limits.api_rate_limit === -1) return 'unlimited';
        return Math.max(0, limits.api_rate_limit - currentUsage);
      default:
        return 0;
    }
  };

  const incrementUsage = async (resourceType: 'lga_report' | 'api_call') => {
    if (!user) return { error: 'No authenticated user' };

    try {
      const { error } = await users.incrementUsage(user.id, resourceType);
      if (error) throw error;

      // Update local state
      setState(prev => ({
        ...prev,
        usage: {
          ...prev.usage,
          [resourceType]: {
            ...prev.usage[resourceType],
            count: (prev.usage[resourceType]?.count || 0) + 1,
          },
        },
      }));

      return { error: null };
    } catch (error) {
      return { error: error instanceof Error ? error.message : 'Failed to increment usage' };
    }
  };

  const getSubscriptionTier = (): SubscriptionTier => {
    return state.subscription?.tier || 'gold';
  };

  const isSubscriptionActive = (): boolean => {
    return state.subscription?.status === 'active';
  };

  const getSubscriptionEndDate = (): Date | null => {
    if (!state.subscription?.current_period_end) return null;
    return new Date(state.subscription.current_period_end);
  };

  const isSubscriptionExpiring = (daysThreshold = 7): boolean => {
    const endDate = getSubscriptionEndDate();
    if (!endDate) return false;

    const now = new Date();
    const diffTime = endDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays <= daysThreshold && diffDays > 0;
  };

  return {
    ...state,
    canAccessResource,
    getRemainingUsage,
    incrementUsage,
    getSubscriptionTier,
    isSubscriptionActive,
    getSubscriptionEndDate,
    isSubscriptionExpiring,
    hasActiveSubscription: !!state.subscription && state.subscription.status === 'active',
  };
};
